import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Box } from '@zillow/constellation-10';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { SatelliteWebLandlordGetAttachmentsMetadataResponseDto } from '@zg-rentals/ts-satellite-client';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationMessageType,
  SatelliteUiTreatmentType,
} from '@zg-rentals/ts-satellite-client';
import { useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata } from '@zg-rentals/ts-satellite-client-react-query';
import { mapAttachmentToMetadataDto } from '../../utils/attachmentMetadataUtils';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import type { ConversationListConversation } from '../ConversationList';
import type { ConversationIdentity } from '../MessageListItems';
import { preventMarkAsReadStore, shouldScrollToRenterProfileStore } from '../../lib/inboxStores';
import { inboxPageShowEmptyContainerStore } from '../../lib/inboxSearchListStores';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import useConversationActions from '../../hooks/useConversationActions';
import useDevices from '../../hooks/useDevices';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { useAuth } from '../../AuthProvider';
import { TRIGGER_LOCATION } from '../../constants/clickstreamEvents/globals';
import AddMessage, { type AddMessageCompletedCacheUpdate } from '../AddMessage';
import type { OnSystemMessageCtaClickArgs } from '../MessageItem';
import EmptyContainer from './components/EmptyContainer';
import MessageListItems from '../MessageListItems';
import { handleRenterProfileClick } from './handleRenterProfileClick';
import { logError } from '@zg-rentals/log-error';
import {
  buildRequestParams,
  getInboxConversationsQueryKey,
  type InboxConversationsInfiniteData,
} from '../../hooks/useInboxConversationsQuery';
import useInboxConversationsQuery from '../../hooks/useInboxConversationsQuery';
import { useConversationMessagesQuery } from '../../hooks/useConversationMessagesQuery';
import { useMarkConversationReadOnMobileBackNav } from './useMarkConversationReadOnMobileBackNav';

const toKnownUiTreatmentType = (value: string | null | undefined): SatelliteUiTreatmentType | undefined =>
  Object.values(SatelliteUiTreatmentType).find((t) => t === value);

export default function MessageList(): JSX.Element {
  const queryClient = useQueryClient();
  const {
    conversationId,
    listingAlias,
    clientTimeZone,
    mobileView,
    folder,
    daysBack,
    isByProperty,
    readRepliedStatus,
    renterProgressStatus,
  } = useContext(InboxPageContext);
  const { changeMobileView } = useInboxRouterHelper();
  const { isDesktopView, isMobileView } = useDevices();
  const { markConversationAsRead } = useConversationActions();
  const { user } = useAuth();
  const inboxPageShowEmptyContainer = inboxPageShowEmptyContainerStore.useValue();
  const preventMarkAsRead = preventMarkAsReadStore.useValue();

  const moveConversationToTop = useCallback(
    (
      targetConversationId: string,
      messageUpdate?: {
        message: string;
        messageDateMs: number;
        isMessageOwner: boolean;
        uiTreatmentType?: SatelliteUiTreatmentType;
      },
    ) => {
      const params = buildRequestParams({
        listingAlias: isByProperty ? listingAlias : null,
        clientTimeZone,
        daysBack,
        folder,
        readRepliedStatus,
        renterProgressStatus,
      });
      const queryKey = getInboxConversationsQueryKey(params);

      queryClient.setQueryData<InboxConversationsInfiniteData>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        const allConversations = oldData.pages.flatMap((page) => page.conversations);
        const found = allConversations.find((conv) => conv.conversationId === targetConversationId);
        if (!found) return oldData;

        const rest = allConversations.filter((conv) => conv.conversationId !== targetConversationId);
        const updated: ConversationListConversation = messageUpdate
          ? {
              ...found,
              mostRecentMessage: {
                message: messageUpdate.message,
                messageDateMs: messageUpdate.messageDateMs,
                isMessageOwner: messageUpdate.isMessageOwner,
                uiTreatmentType: messageUpdate.uiTreatmentType ?? SatelliteUiTreatmentType.HIGH_PRIORITY,
              },
            }
          : found;

        return {
          ...oldData,
          pages: [
            {
              conversations: [updated, ...rest],
              hasNextPage: oldData.pages[oldData.pages.length - 1]?.hasNextPage ?? false,
            },
          ],
          pageParams: [oldData.pageParams[0]],
        };
      });
    },
    [
      clientTimeZone,
      daysBack,
      folder,
      isByProperty,
      listingAlias,
      readRepliedStatus,
      renterProgressStatus,
      queryClient,
    ],
  );

  const handleResendMessageComplete = useCallback(
    ({ conversation }: { conversation: ConversationIdentity }) => {
      moveConversationToTop(conversation.conversationId);
    },
    [moveConversationToTop],
  );

  // Cache-first read — MessageListItems owns the network fetch + polling. Reading from the same
  // React Query cache means we don't double-fetch.
  const { data: conversationData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
  });

  // `isPhoneLead` isn't returned by the `getMessages` Proxit endpoint, so we read it from
  // the inbox conversations cache (which exposes `isPhoneLead` per conversation). The hook
  // is rendered alongside ConversationList on the inbox page, so the cache is already
  // populated. We subscribe with `enabled: false` to avoid triggering a second fetch.
  const { data: inboxConversationsData } = useInboxConversationsQuery({ enabled: false });
  const isPhoneLead = useMemo(
    () => inboxConversationsData?.conversations.find((c) => c.conversationId === conversationId)?.isPhoneLead ?? false,
    [inboxConversationsData, conversationId],
  );

  const messageListContainerRef = useRef<HTMLDivElement>(null);
  const addMessageContainerRef = useRef<HTMLDivElement>(null);

  const backNavMessages = conversationData?.conversation;
  useMarkConversationReadOnMobileBackNav({
    mobileView,
    isDesktopView,
    preventMarkAsRead,
    hasUnreadMessage: conversationData?.hasUnreadMessage ?? false,
    lastMessageDateMs: backNavMessages?.length ? backNavMessages[backNavMessages.length - 1].messageDateMs : undefined,
    markConversationAsRead,
    clearPreventMarkAsRead: () => preventMarkAsReadStore.set(false),
  });

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    if (window.ResizeObserver && addMessageContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        if (messageListContainerRef.current) {
          messageListContainerRef.current.scrollTo(0, messageListContainerRef.current.scrollHeight);
        }
      });
      resizeObserver.observe(addMessageContainerRef.current);
    }
    return () => {
      addMessageContainerRef.current && resizeObserver?.unobserve(addMessageContainerRef.current);
      resizeObserver?.disconnect();
    };
  }, [conversationId]);

  const conversation = useMemo(
    (): ConversationIdentity => ({
      conversationId: conversationId ?? '',
      listingAlias,
      clientTimeZone,
    }),
    [conversationId, listingAlias, clientTimeZone],
  );

  const handleAddMessageCompletedCacheUpdate = useCallback(
    ({ newMessage, attachmentsForMessage }: AddMessageCompletedCacheUpdate) => {
      // Sending a message is an explicit engagement, so clear any "mark unread" suppression —
      // the send-triggered scroll-to-bottom then marks the conversation read. Without this, a
      // conversation marked unread stays stuck unread after replying (the flag is otherwise only
      // reset on conversation switch, or, on mobile, on re-opening the conversation).
      preventMarkAsReadStore.set(false);

      if (!newMessage?.messageId?.toString().includes('-') && folder) {
        const messageUpdate = newMessage.messagePreview
          ? {
              message: newMessage.messagePreview,
              messageDateMs: Number(newMessage.messageDateMs),
              // A landlord always owns the message they just sent; the Proxit response omits this field.
              isMessageOwner: true,
              uiTreatmentType: toKnownUiTreatmentType(newMessage.uiTreatmentType),
            }
          : undefined;
        try {
          moveConversationToTop(conversationId ?? '', messageUpdate);
        } catch (error) {
          logError({
            error,
            context: {
              details: 'AddMessage - Failed to update conversation list cache after sending message',
              conversationId,
              listingAlias,
            },
          });
        }

        if (attachmentsForMessage.length) {
          try {
            const queryKey = useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata.getKey({
              conversationId: conversationId!,
            });
            const existingData =
              queryClient.getQueryData<SatelliteWebLandlordGetAttachmentsMetadataResponseDto>(queryKey);
            const existingDtos = existingData?.attachmentMetadataDtos ?? [];
            const newDtos = attachmentsForMessage.map(mapAttachmentToMetadataDto);
            queryClient.setQueryData<SatelliteWebLandlordGetAttachmentsMetadataResponseDto>(queryKey, {
              attachmentMetadataDtos: [...newDtos, ...existingDtos],
            });
          } catch (error) {
            logError({
              error,
              context: {
                details: 'AddMessage - Failed to update getAttachmentsMetadata cache after sending message',
                conversationId,
                listingAlias,
                attachmentsCount: attachmentsForMessage.length,
              },
            });
          }
        }
      }
    },
    [
      conversationId,
      listingAlias,
      clientTimeZone,
      daysBack,
      folder,
      isByProperty,
      queryClient,
      readRepliedStatus,
      renterProgressStatus,
      moveConversationToTop,
    ],
  );

  if (!conversationId) {
    return <EmptyContainer />;
  }

  const showEmptyContainer = inboxPageShowEmptyContainer;
  const fullMessageCountNumber = conversationData?.pagination.queryFullMessageCount ?? 0;
  const hasQuickResponses = conversationData?.hasQuickResponses ?? false;
  const quickResponses = conversationData?.quickResponses ?? null;
  const isConversationBlockedByZillow = conversationData?.isConversationBlockedByZillow ?? false;
  const isSpam = conversationData?.status?.isSpam ?? false;
  const showMoveToInboxBanner = folder === SatelliteConversationFolderTypeV2.SPAM && !isConversationBlockedByZillow;

  const handleScrollComplete = () => {
    if (preventMarkAsRead || !conversationData?.hasUnreadMessage) return;
    if (!isDesktopView && mobileView !== InboxPageMobileView.MessageList) return;
    const messages = conversationData.conversation;
    if (messages?.length) {
      markConversationAsRead(messages[messages.length - 1].messageDateMs);
    }
  };

  const onRenterProfileClick = () => handleRenterProfileClick({ isMobileView, user, listingAlias, changeMobileView });

  const handleSystemMessageCtaClick = ({ messageType, url }: OnSystemMessageCtaClickArgs) => {
    if (messageType === SatelliteConversationMessageType.RENTER_PROFILE_SUBMISSION) {
      if (isMobileView) {
        changeMobileView(InboxPageMobileView.RenterProfile);
      }
      shouldScrollToRenterProfileStore.set(true);
      return;
    }
    window.open(url, '_blank');
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        minHeight: 0,
      }}
    >
      <MessageListItems
        ref={messageListContainerRef}
        conversation={conversation}
        showEmptyContainer={showEmptyContainer}
        shouldRefetchAttachments={true}
        showMoveToInboxBanner={showMoveToInboxBanner}
        onResendMessageComplete={handleResendMessageComplete}
        onConversationChange={() => preventMarkAsReadStore.set(false)}
        onRenterProfileClick={onRenterProfileClick}
        onSystemMessageCtaClick={handleSystemMessageCtaClick}
        onScrollComplete={handleScrollComplete}
        triggerLocation={TRIGGER_LOCATION.IreneInbox}
        isMessageListVisible={isDesktopView || mobileView === InboxPageMobileView.MessageList}
      />
      <AddMessage
        ref={addMessageContainerRef}
        conversationId={conversationId!}
        listingAlias={listingAlias!}
        clientTimeZone={clientTimeZone}
        fullMessageCountNumber={fullMessageCountNumber}
        hasQuickResponses={hasQuickResponses}
        quickResponses={quickResponses}
        isConversationBlockedByZillow={isConversationBlockedByZillow}
        isPhoneLead={isPhoneLead}
        isSpam={isSpam}
        triggerLocation={TRIGGER_LOCATION.IreneInbox}
        onAddMessageCompletedCacheUpdate={handleAddMessageCompletedCacheUpdate}
      />
    </Box>
  );
}
